"use client"

import { AppBar, Box, IconButton, Toolbar } from "@mui/material"
import { Desktop, Mobile } from "common/MediaQueries"
import {
  HeaderLinkListDesktop,
  HeaderLinkListMobile,
  NestedNavigationLinksType,
} from "common/SiteLinks"
import NextLink from "common/bit/NextLink"
import useMounted from "common/useMounted"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import HamburgerIcon from "reusable/icons/HamburgerIcon"
import XIcon from "reusable/icons/XIcon"
import styled from "styled-components"
import {
  HEADER_ANIME_D,
  HEADER_ANIME_M,
  HEADER_HEIGHT_D,
  HEADER_HEIGHT_M,
  HEADER_HEIGHT_SMALL_LAPTOP,
  HomePageContainer,
} from "theme/shared"
import HeaderLinkList from "./HeaderLinkList"
import HeaderSubCollapse from "./HeaderSubCollapse"
import HeaderTopButtons from "./HeaderTopButtons"
import SideDrawer from "./sideDrawer/SideDrawer"

const StyledAppBar = styled(AppBar)`
  justify-content: center;
  background-color: ${(p) => p.theme.colors.headerColor};
  transition: transform 330s ease-in-out;
  z-index: 1250 !important;
`
const StyledContainer = styled(HomePageContainer)<{
  $narrow: boolean
  $mounted: boolean
}>`
  height: ${(P) => (P.$narrow ? HEADER_ANIME_M : HEADER_HEIGHT_M)}px;
  transition: ${(P) => (P.$mounted ? "height 0.33s linear" : "none")};
  ${(p) =>
    p.theme.breakpoints.down("smallLaptop") && p.theme.breakpoints.up("md")} {
    height: ${(P) =>
      P.$narrow ? HEADER_ANIME_D : HEADER_HEIGHT_SMALL_LAPTOP}px;
    min-height: fit-content;
  }
  ${(p) => p.theme.breakpoints.up("smallLaptop")} {
    height: ${(P) => (P.$narrow ? HEADER_ANIME_D : HEADER_HEIGHT_D)}px;
    padding: 0 16px;
  }
  ${(p) => p.theme.breakpoints.up("mediumLaptop")} {
    padding: 0 88px;
  }
`
const StyledToolbar = styled(Toolbar).attrs({ disableGutters: true })`
  height: 100%;
  width: 100%;
  justify-content: space-between;
  min-height: auto;
  align-items: center;
  gap: 24px;
  ${(p) => p.theme.breakpoints.down("smallLaptop")} {
    gap: 16px;
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    gap: 24px;
  }
`
const StyledLogoBox = styled(Box)<{ $isNarrow: boolean; $mounted: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  // on desktop, if $nerrow is true scale logo down with transition similar to header transition
  ${(p) => p.theme.breakpoints.up("md")} {
    transform: ${(P) => (P.$isNarrow ? "scale(0.8)" : "scale(1)")};
    transition: ${(P) => (P.$mounted ? "transform 0.33s linear" : "none")};
    ${(p) => p.theme.breakpoints.down("smallLaptop")} {
      img {
        width: 64px;
        height: 64px;
        min-width: 64px;
        min-height: 64px;
      }
    }
  }
`
const IconWrapper = styled(Box)`
  width: 32px;
  height: 32px;
  margin-left: 16px;
`
const StyledMobile = styled(Mobile)`
  width: 100%;
`

const StyledButtonDesktop = styled(Desktop)`
  margin-right: auto;
  flex-shrink: 0;
`

type HeaderProps = {
  narrow?: boolean
  className?: string
  links?: NestedNavigationLinksType[]
  hamburgerLink?: string
  isShopHeader?: boolean
}

const Header = ({
  narrow = false,
  className,
  links,
  isShopHeader = false,
}: HeaderProps) => {
  const isMounted = useMounted()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [subLinks, setSubLinks] = useState(null)
  const appBarRef = useRef<HTMLElement>(null)

  // Publish the header bar's real (expanded) height as --header-height so page
  // wrappers can offset content by the actual header size instead of a
  // hardcoded value. The bar grows when the nav wraps at laptop widths, so a
  // fixed offset leaves the header overlapping (or a white gap under) the
  // content (DLK-809). We measure the first child (the bar) — not the AppBar —
  // to exclude the hover submenu, and only while at the top of the page so the
  // scroll-shrink animation never reflows the content.
  useEffect(() => {
    const bar = appBarRef.current?.firstElementChild as HTMLElement | null
    if (!bar) return
    const setHeaderHeightVar = () => {
      if (window.scrollY > 4) return
      document.documentElement.style.setProperty(
        "--header-height",
        `${Math.round(bar.getBoundingClientRect().height)}px`
      )
    }
    setHeaderHeightVar()
    const observer = new ResizeObserver(setHeaderHeightVar)
    observer.observe(bar)
    window.addEventListener("resize", setHeaderHeightVar)
    window.addEventListener("scroll", setHeaderHeightVar, { passive: true })
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", setHeaderHeightVar)
      window.removeEventListener("scroll", setHeaderHeightVar)
    }
  }, [])

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return
      }
      setIsDrawerOpen(open)
    }

  const getLinks = (links, isDesktop) => {
    if (links) return links
    if (isDesktop) return HeaderLinkListDesktop
    return HeaderLinkListMobile
  }

  return (
    <>
      <StyledAppBar ref={appBarRef} className={className} suppressHydrationWarning>
        <StyledContainer
          $narrow={isMounted ? narrow : false}
          $mounted={isMounted}
        >
          <StyledToolbar>
            {!isShopHeader && (
              <Mobile aboveTheFold>
                <IconWrapper>
                  <IconButton
                    onClick={toggleDrawer(!isDrawerOpen)}
                    aria-label={
                      isDrawerOpen ? "סגירת תפריט" : "לחץ לפתיחת תפריט ראשי"
                    }
                    color="primary"
                    style={{ padding: 0 }}
                    aria-expanded={isDrawerOpen ? true : false}
                  >
                    {isDrawerOpen ? <XIcon /> : <HamburgerIcon />}
                  </IconButton>
                </IconWrapper>
              </Mobile>
            )}
            <NextLink href="/">
              <StyledLogoBox
                $isNarrow={isMounted ? narrow : false}
                $mounted={isMounted}
              >
                {/* Desktop Logo */}
                <Desktop>
                  <Image
                    src="/img/new-white-logo-v2.png"
                    alt="mazda-logo"
                    width={85}
                    height={85}
                    priority
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      objectFit: "contain",
                      aspectRatio: "1/1",
                    }}
                  />
                </Desktop>
                {/* Mobile Logo */}
                <Mobile>
                  <Image
                    src="/img/new-white-logo-v2.png"
                    alt="mazda-logo"
                    width={45}
                    height={45}
                    priority
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      objectFit: "contain",
                      aspectRatio: "1/1",
                    }}
                  />
                </Mobile>
              </StyledLogoBox>
            </NextLink>
            {isShopHeader && (
              <StyledMobile aboveTheFold>
                <HeaderLinkList
                  linkList={getLinks(links, false)}
                  setSubLinks={setSubLinks}
                  isShopHeader={isShopHeader}
                  closeDrawer={() => {
                    setIsDrawerOpen(false)
                  }}
                />
              </StyledMobile>
            )}
            <Desktop aboveTheFold>
              <HeaderLinkList
                linkList={getLinks(links, true)}
                setSubLinks={setSubLinks}
                isShopHeader={isShopHeader}
              />
            </Desktop>
            <StyledButtonDesktop aboveTheFold>
              <HeaderTopButtons setSubLinks={setSubLinks} />
            </StyledButtonDesktop>
          </StyledToolbar>
        </StyledContainer>
        <Desktop aboveTheFold>
          <HeaderSubCollapse subLinks={subLinks} />
        </Desktop>
      </StyledAppBar>
      <Mobile>
        <SideDrawer
          isOpen={isDrawerOpen}
          toggleDrawer={toggleDrawer}
          links={links}
        />
      </Mobile>
    </>
  )
}

export default Header
